import { logger } from '@loc/helpers/src/logger/client'
import { useLatest, useMemoizedFn } from 'ahooks'

import { useMountIdle } from '../idle'

import type { HowlOptions } from 'howler'

export type HowlClass = typeof import('howler').Howl
export type Howl = import('howler').Howl

export function useHowler(onLoad: (Howl: HowlClass) => void) {
  const howlClassRef = useRef<HowlClass>()

  useMountIdle(() => {
    import('howler').then((module) => {
      howlClassRef.current = module.default.Howl
      onLoad(module.default.Howl)
    })
  })
}

export type CustomHowlOptions = Pick<HowlOptions, 'src' | 'volume' | 'loop'>

export interface UseAudioOptions extends CustomHowlOptions {
  onLoad?: (howl: Howl) => void
}

export function useAudio(options: UseAudioOptions) {
  const { src, volume = 1, loop = false, onLoad } = options
  const howlClassRef = useRef<HowlClass>()
  const howlRef = useRef<Howl>()
  const [duration, setDuration] = useState<number | null>(null)
  const [currentTime, setCurrentTime] = useState<number | null>(null)
  const [error, setError] = useState<unknown>()
  const [loading, setLoading] = useState(false)
  const [playing, setPlaying] = useState(false)

  const onLoadLatest = useLatest(onLoad)

  const createHowler = useMemoizedFn((options: CustomHowlOptions) => {
    if (!howlClassRef.current) {
      return
    }
    setLoading(true)
    const HowlClass = howlClassRef.current
    const howl = new HowlClass({
      ...options,
      html5: true,
    })

    howl.on('load', () => {
      setDuration(howl.duration())
      setLoading(false)
      onLoadLatest.current?.(howl)
    })

    howl.on('loaderror', (soundId, error) => {
      logger.error(`Load audio [${options.src}] error`, error)
      setError(error)
      setLoading(false)
    })

    const handlePlay = () => {
      setCurrentTime(howl.seek())

      if (howl.playing()) {
        if (!playing) {
          setPlaying(true)
        }
        requestAnimationFrame(handlePlay)
      }
    }

    howl.on('play', handlePlay)

    howl.on('pause', () => {
      setPlaying(false)
    })

    howl.on('end', () => {
      setCurrentTime(null)
      setPlaying(false)
    })

    howl.on('stop', () => {
      setCurrentTime(null)
      setPlaying(false)
    })

    howlRef.current = howl
  })

  useHowler((Howl) => {
    howlClassRef.current = Howl
    if (!src) {
      return
    }
    createHowler({ src, volume, loop })
  })

  useEffect(() => {
    if (!src) {
      return
    }
    createHowler({ src, volume, loop })

    return () => {
      howlRef.current?.unload()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(src)])

  useEffect(() => {
    if (!howlRef.current) {
      return
    }
    howlRef.current.volume(volume)
  }, [volume])

  useEffect(() => {
    if (!howlRef.current) {
      return
    }
    howlRef.current.loop(loop)
  }, [loop])

  const togglePlay = useMemoizedFn(() => {
    if (!howlRef.current) {
      return
    }
    if (howlRef.current.playing()) {
      howlRef.current.pause()
    } else {
      howlRef.current.play()
    }
  })

  const playFrom = useMemoizedFn((timeInSecond = 0) => {
    if (!howlRef.current) {
      return
    }
    howlRef.current.seek(timeInSecond)
    howlRef.current.play()
  })

  const togglePlayFrom = useMemoizedFn((timeInSecond = 0) => {
    if (!howlRef.current) {
      return
    }
    if (howlRef.current.playing()) {
      howlRef.current.pause()
    } else {
      playFrom(timeInSecond)
    }
  })

  /** 有资源地址立即播放时使用 setTimeout 触发播放不稳定，暂不请求具体原因，通过递归调用解决 */
  const playIt = useMemoizedFn(() => {
    setTimeout(() => {
      howlRef.current?.play()

      if (!howlRef.current?.playing) {
        playIt()
      }
    })
  })

  const playDuration = useMemoizedFn((start: number, end: number) => {
    if (!howlRef.current) {
      return
    }
    // 重置播放时间，不重置会导致 onPlay 回调中 seek() 返回值不更新
    howlRef.current.seek(0)
    playFrom(start)

    const handlePlay = () => {
      if (!howlRef.current) {
        return
      }
      if (howlRef.current.seek() >= end) {
        howlRef.current?.pause()
        howlRef.current?.off('play', handlePlay)
      } else {
        requestAnimationFrame(handlePlay)
      }
    }

    howlRef.current.on('play', handlePlay)
  })

  const togglePlayDuration = useMemoizedFn((start: number, end: number) => {
    if (!howlRef.current) {
      return
    }

    if (howlRef.current.playing()) {
      howlRef.current.pause()
    } else {
      playDuration(start, end)
    }
  })

  return {
    howlRef,
    duration,
    currentTime,
    error,
    loading,
    playing,
    togglePlay,
    playFrom,
    togglePlayFrom,
    playIt,
    playDuration,
    togglePlayDuration,
  }
}
